import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { CrosshairSimple, ArrowRight } from "@phosphor-icons/react";

export default function Register() {
  const { register, loading } = useAuth();
  const navigate = useNavigate();
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      await register(email, password, fullName);
      toast.success("Account deployed. Welcome, operator.");
      navigate("/dashboard");
    } catch (e) {
      const msg = e.response?.data?.detail || "Registration failed";
      setErr(msg);
      toast.error(msg);
    }
  };

  return (
    <div className="min-h-screen bg-[#0D0D0D] flex flex-col" data-testid="register-page">
      <div className="grid-overlay absolute inset-0 opacity-30 pointer-events-none" />
      <div className="relative flex-1 flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-md">
          <Link to="/" className="inline-flex items-center gap-3 mb-12" data-testid="register-logo">
            <div className="w-9 h-9 bg-[#FF3366] flex items-center justify-center">
              <CrosshairSimple size={20} weight="bold" color="#fff" />
            </div>
            <div className="leading-none">
              <div className="font-heading font-black uppercase tracking-tighter text-xl">Systemic Friction</div>
              <div className="text-[10px] font-mono-sf uppercase tracking-[0.25em] text-neutral-500">Command Center</div>
            </div>
          </Link>

          <div className="font-mono-sf text-xs uppercase tracking-[0.3em] text-[#FF3366] mb-3">// deploy</div>
          <h1 className="font-heading font-black uppercase tracking-tighter text-5xl mb-2">Enlist.</h1>
          <p className="text-neutral-400 text-sm mb-10">Build your first case in under five minutes.</p>

          <form onSubmit={onSubmit} className="space-y-5" data-testid="register-form">
            <div>
              <label className="block font-mono-sf text-[10px] uppercase tracking-[0.25em] text-neutral-500 mb-2">Full Name</label>
              <input
                required
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="J. Citizen"
                className="w-full h-12 bg-[#0D0D0D] border border-[#1A1A1A] focus:border-[#FF3366] focus:ring-2 focus:ring-[#FF3366] focus:outline-none px-4 text-white placeholder:text-neutral-600 font-mono-sf text-sm transition-colors"
                data-testid="register-name-input"
              />
            </div>
            <div>
              <label className="block font-mono-sf text-[10px] uppercase tracking-[0.25em] text-neutral-500 mb-2">Email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@domain.com"
                className="w-full h-12 bg-[#0D0D0D] border border-[#1A1A1A] focus:border-[#FF3366] focus:ring-2 focus:ring-[#FF3366] focus:outline-none px-4 text-white placeholder:text-neutral-600 font-mono-sf text-sm transition-colors"
                data-testid="register-email-input"
              />
            </div>
            <div>
              <label className="block font-mono-sf text-[10px] uppercase tracking-[0.25em] text-neutral-500 mb-2">Password (min 6)</label>
              <input
                type="password"
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full h-12 bg-[#0D0D0D] border border-[#1A1A1A] focus:border-[#FF3366] focus:ring-2 focus:ring-[#FF3366] focus:outline-none px-4 text-white placeholder:text-neutral-600 font-mono-sf text-sm transition-colors"
                data-testid="register-password-input"
              />
            </div>

            {err && (
              <div className="border border-[#FF3366] bg-[#FF3366]/10 p-3 font-mono-sf text-xs text-[#FF3366] uppercase tracking-wider" data-testid="register-error">
                {err}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-[#FF3366] hover:bg-[#FF5248] disabled:opacity-50 text-white font-mono-sf text-sm uppercase tracking-[0.25em] font-bold flex items-center justify-center gap-3 transition-colors"
              data-testid="register-submit-btn"
            >
              {loading ? "Provisioning..." : <>Deploy Account <ArrowRight size={16} weight="bold" /></>}
            </button>
          </form>

          <div className="mt-8 text-center text-sm text-neutral-500 font-mono-sf">
            Already enlisted?{" "}
            <Link to="/login" className="text-[#FF3366] hover:text-[#FF5248] uppercase tracking-wider" data-testid="register-to-login">
              Log in &rarr;
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
